/**
 * マニュアルの完成度。analysis.json から、レビューの優先順位づけに使う指標を取り出す。
 *
 * **どれも analysis.json に既にある値で、ここでは新しく判断しない。** 画面に出す目的は
 * 「どの品目から手を付けるか」を人が決められるようにすること。撮影率が低い品目は
 * SOP に「撮影範囲外」が並び、レシピ・メモでの補足が要る。
 */

export interface PrerequisiteLike {
  name?: string | null;
  ingredient_id?: string | null;
  /** 別途仕込みSOPが紐づいているか */
  linked_sop?: string | null;
  /** 市販品など、作り方を記録しなくてよいもの */
  external?: boolean | null;
}

export interface AnalysisLike {
  video?: { duration_sec?: number | null } | null;
  timing?: { uncovered_sec?: number | null } | null;
  steps?: unknown[] | null;
  ingredients_master?: unknown[] | null;
  prerequisites?: PrerequisiteLike[] | null;
  consensus?: { disputed?: unknown[] | null } | null;
  warnings?: unknown[] | null;
  total_active_time_sec?: number | null;
}

/** 完成度の段階。レビューの着手順に対応する */
export type Tier = "A" | "B" | "C" | "D";

export const TIER_LABEL: Record<Tier, string> = {
  A: "そのまま読める",
  B: "軽い補足で足りる",
  C: "補足が要る",
  D: "素材追加を検討",
};

export const TIER_HELP: Record<Tier, string> = {
  A: "撮影率が高く、仕込み済み食材の欠落もほぼありません。確認事項に答えるだけで確定できます。",
  B: "未記録は数分以内です。指摘の中心は「仕込み済み食材の作り方が別途必要」です。",
  C: "未記録区間が長いか、仕込み済み食材が多く残っています。レシピ・加藤メモでの補足が要ります。",
  D: "未記録区間が長く、かつ仕込み済み食材が多い状態です。映像の追加なしに完成させるのは難しい可能性があります。",
};

export interface Completeness {
  steps: number;
  durationSec: number;
  activeSec: number;
  /** 撮影率(0〜1)。動画の尺が読めなければ null */
  coveredRatio: number | null;
  uncoveredSec: number;
  ingredients: number;
  /** 仕込みSOPが無く、市販品指定もされていない食材の名前 */
  missingPrep: string[];
  /** 合議で判断が割れ、人の確定が要る項目 */
  disputed: number;
  warnings: number;
  tier: Tier;
}

function tierOf(coveredRatio: number | null, missingPrep: number): Tier {
  // 撮影率が読めない品目は、撮影率の条件を満たさないものとして扱う（甘く見せない）
  const cov = coveredRatio ?? 0;
  if (cov >= 0.95 && missingPrep <= 2) return "A";
  if (cov >= 0.8 && missingPrep <= 7) return "B";
  if (cov >= 0.6 || missingPrep <= 10) return "C";
  return "D";
}

export function completenessOf(a: AnalysisLike): Completeness {
  const durationSec = a.video?.duration_sec ?? 0;
  const uncoveredSec = a.timing?.uncovered_sec ?? 0;
  const prerequisites = a.prerequisites ?? [];
  // 「仕込みSOP未作成」= 動画外で出来ていた食材のうち、作り方が分からないもの。
  // 市販品(external)や別SOPに紐づくもの(linked_sop)は数えない
  const missingPrep = prerequisites
    .filter((p) => !p.linked_sop && !p.external)
    .map((p) => p.name ?? p.ingredient_id ?? "?");
  const coveredRatio = durationSec > 0 ? Math.max(0, 1 - uncoveredSec / durationSec) : null;
  return {
    steps: (a.steps ?? []).length,
    durationSec,
    activeSec: a.total_active_time_sec ?? 0,
    coveredRatio,
    uncoveredSec,
    ingredients: (a.ingredients_master ?? []).length,
    missingPrep,
    disputed: (a.consensus?.disputed ?? []).length,
    warnings: (a.warnings ?? []).length,
    tier: tierOf(coveredRatio, missingPrep.length),
  };
}

/** 秒を「N分」に。0分でも「0分」と出す（空欄より意味が伝わる） */
export function minutes(sec: number): string {
  return `${Math.round(sec / 60)}`;
}
